import React, { useContext, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import bkstApi from "../../api";
import EmailInput from "../../common/component/EmailInput";
import CloudinaryUploadWidget from "../../common/component/CloudinaryUploadWidget";
import { UserContext } from "../../context/UserContext";
import { hasOwnerAccess, isEmailValid } from "../../common/util";

const ALL_ROLES = ["owner", "manager", "clerk", "kitchen", "kitchen leader"];

const ROLE_DESCRIPTIONS = {
  owner: "Full access to quotes, orders, catalog, settings, reports, and plan.",
  manager: "Day-to-day ops for quotes, orders, and catalog — without reports or plan.",
  clerk: "Point-of-sale access to create and manage orders.",
  kitchen: "Kitchen display for production — view and progress orders.",
  "kitchen leader":
    "Kitchen display plus order edits, payments, and refunds on the floor.",
};

function formatRoleLabel(role) {
  return role
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export default function AddStaff(props) {
  const { user } = useContext(UserContext);
  const isOwner = hasOwnerAccess(user?.role);
  const roles = isOwner ? ALL_ROLES : ALL_ROLES.filter((role) => role !== "owner");
  const storeLocations = user?.config?.storeLocation || [];
  const showStoreLocations =
    user?.capabilities?.includes("multi-location") && storeLocations.length > 1;

  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState([]);

  const [photo, setPhoto] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [storeLocationIds, setStoreLocationIds] = useState([]);

  const emailRequired = role === "owner" || role === "manager" || role === "kitchen leader";
  const accessCodeRequired = role === "clerk";
  const showEmail = emailRequired;
  const showAccessCode = Boolean(role);
  const showLocationPicker = showStoreLocations && Boolean(role);

  const resetForm = () => {
    setPhoto("");
    setFirstName("");
    setLastName("");
    setRole("");
    setEmail("");
    setAccessCode("");
    setStoreLocationIds([]);
    setErrors([]);
  };

  const handleRoleChange = (nextRole) => {
    setRole(nextRole);
    setEmail("");
    setAccessCode("");
    setErrors([]);
  };

  const handleAccessCodeChange = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 4);
    setAccessCode(digits);
  };

  const validate = () => {
    const arr = [];
    if (!firstName.trim()) {
      arr.push("First name is required");
    }
    if (!lastName.trim()) {
      arr.push("Last name is required");
    }
    if (!role) {
      arr.push("Role is required");
    }
    if (emailRequired && !email.trim()) {
      arr.push("Email is required for owner, manager, and kitchen leader roles");
    }
    if (email && !isEmailValid(email)) {
      arr.push("Email is invalid");
    }
    if (accessCodeRequired && accessCode.length !== 4) {
      arr.push("Access code must be 4 digits");
    }
    if (!accessCodeRequired && accessCode && accessCode.length !== 4) {
      arr.push("Access code must be 4 digits");
    }
    return arr;
  };

  const handleOpen = () => {
    resetForm();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const submit = (e) => {
    e.preventDefault();
    const arr = validate();
    setErrors(arr);
    if (arr.length > 0) {
      return;
    }

    setProcessing(true);
    bkstApi
      .post(`/my/staff`, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
        email: email.trim(),
        accessCode: accessCode.trim(),
        photo,
        storeLocationIds: showLocationPicker ? storeLocationIds : undefined,
      })
      .then((res) => {
        setProcessing(false);
        handleClose();
        if (props.onCreated) {
          props.onCreated(res.data.staff);
        }
      })
      .catch((err) => {
        setProcessing(false);
        setErrors([err.response?.data?.message || "Failed to create staff"]);
      });
  };

  return (
    <>
      <Tooltip title="Add Staff">
        <IconButton
          color="primary"
          onClick={handleOpen}
          sx={{
            width: 48,
            height: 48,
            bgcolor: "primary.main",
            color: "white",
            "&:hover": {
              bgcolor: "primary.dark",
            },
          }}
        >
          <AddIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Tooltip>
      <Dialog fullScreen={true} open={open} scroll="paper">
        <form onSubmit={submit} style={{ display: "contents" }}>
          <DialogTitle
            sx={{
              padding: "7px 20px",
              display: "flex",
              gap: "10px",
              alignItems: "center",
              margin: "0",
              backgroundColor: "#DEDEDE",
              borderBottom: "3px solid #DEDEDE",
            }}
          >
            <IconButton onClick={handleClose}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">Add Staff</Typography>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: "#F7F7F7" }}>
            <Container maxWidth="md">
              {errors.length > 0 && (
                <Box my={2}>
                  <Alert severity="error">{errors.join(". ")}</Alert>
                </Box>
              )}
              <Box py={4}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={3} md={2}>
                    <Box>
                      <Box
                        sx={{
                          width: "100%",
                          aspectRatio: "1/1",
                          backgroundColor: "#ECECEC",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          mb: 1,
                        }}
                      >
                        {photo ? (
                          <img
                            src={photo}
                            alt=""
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : null}
                      </Box>
                      <CloudinaryUploadWidget
                        folder={`s/${user.acctId}/staff`}
                        onComplete={(images) => setPhoto(images?.[0] || "")}
                      >
                        <Button variant="contained" color="secondary" fullWidth>
                          Upload
                        </Button>
                      </CloudinaryUploadWidget>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={9} md={10}>
                    <Paper elevation={0}>
                      <Box px={2} py={4}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              value={firstName}
                              variant="outlined"
                              fullWidth
                              label="First Name"
                              onChange={(e) => setFirstName(e.target.value)}
                              required
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              value={lastName}
                              variant="outlined"
                              fullWidth
                              label="Last Name"
                              onChange={(e) => setLastName(e.target.value)}
                              required
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControl fullWidth required>
                              <InputLabel id="staff-role-label">Role</InputLabel>
                              <Select
                                labelId="staff-role-label"
                                value={role}
                                label="Role"
                                onChange={(e) => handleRoleChange(e.target.value)}
                                renderValue={(selected) => formatRoleLabel(selected)}
                              >
                                {roles.map((option) => (
                                  <MenuItem key={option} value={option} sx={{ py: 1.5 }}>
                                    <Box>
                                      <Typography variant="body1">
                                        {formatRoleLabel(option)}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        {ROLE_DESCRIPTIONS[option]}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          {role && (
                            <>
                              {showEmail && (
                                <Grid item xs={12}>
                                  <EmailInput
                                    value={email}
                                    onChange={(val) => setEmail(val)}
                                    required={emailRequired}
                                  />
                                </Grid>
                              )}
                              {showAccessCode && (
                                <Grid item xs={12}>
                                  <TextField
                                    value={accessCode}
                                    variant="outlined"
                                    fullWidth
                                    label="Access Code"
                                    placeholder="Access Code"
                                    inputProps={{
                                      inputMode: "numeric",
                                      pattern: "[0-9]*",
                                      maxLength: 4,
                                    }}
                                    onChange={(e) => handleAccessCodeChange(e.target.value)}
                                    required={accessCodeRequired}
                                    helperText={
                                      accessCodeRequired
                                        ? "Required 4-digit code for login"
                                        : "Optional 4-digit code for POS / KDS login"
                                    }
                                  />
                                </Grid>
                              )}
                              {showLocationPicker && (
                                <Grid item xs={12}>
                                  <FormControl fullWidth>
                                    <InputLabel id="staff-locations-label">
                                      Store Locations
                                    </InputLabel>
                                    <Select
                                      labelId="staff-locations-label"
                                      multiple
                                      value={storeLocationIds}
                                      onChange={(e) => setStoreLocationIds(e.target.value)}
                                      input={<OutlinedInput label="Store Locations" />}
                                      renderValue={(selected) =>
                                        storeLocations
                                          .filter((loc) => selected.includes(loc.id))
                                          .map((loc) => loc.address?.street)
                                          .join(", ")
                                      }
                                    >
                                      {storeLocations.map((loc) => (
                                        <MenuItem key={loc.id} value={loc.id}>
                                          <Checkbox
                                            checked={storeLocationIds.includes(loc.id)}
                                          />
                                          <ListItemText
                                            primary={loc.address?.street || loc.id}
                                          />
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              )}
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Paper>
                    <Box my={2}>
                      <Button type="submit" fullWidth color="primary" variant="contained">
                        ADD STAFF
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Container>
          </DialogContent>
        </form>
        {processing && (
          <Backdrop open={true} style={{ zIndex: "1000", color: "#fff" }}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </Dialog>
    </>
  );
}
